import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Zap, Sword, Swords, Users, Shield, Globe, Book, Crown, Skull, Gem, Coins, Scroll,
  Store, User, BookOpen, Map as MapIcon, Activity, AlertTriangle, ArrowRight, Brain,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSystem } from '../context/SystemContext';
import { vttConfigured } from '../config/vtt';
import { getActiveSystem } from '../config/system';
import { allFamilies, isBuiltInSystem, getFamilyByKey } from '../config/familyRegistry';
import apiClient from '../config/axios';
import OmniSearch from '../components/OmniSearch';
import HomeDashboard from '../components/HomeDashboard';
import BuildRulesTile from '../components/BuildRulesTile';

// The homepage (issue 1587). For a new visitor: what the site is in five
// seconds, the rules searchable from the first screen, and a clear Start free.
// Signed in, the returning player's dashboard (`components/HomeDashboard.js`),
// which reuses this page's tiles, tools and styles.

// The rules, in the two groups the issue names (the header's Character
// Building and World Building). Presentation only: a kind is a tile when the
// system has a Family at its segment, under that Family's name (issue 1549),
// so a homebrew or third system shows only what it has. Traps appear the day
// a system has a `traps` Family (#1584); psionic powers the day it has a
// `powers` Family (qa-runs#1435).
const features = [
  { group: 'character', name: 'Classes',   href: '/classes',   icon: Users,  description: 'Classes with their levels, skills and requirements' },
  { group: 'character', name: 'Races',     href: '/races',     icon: Shield, description: 'Playable races, their traits and favoured classes' },
  { group: 'character', name: 'Feats',     href: '/feats',     icon: Sword,  description: 'Prerequisites, benefits and categories', count: (n) => `${n} with prerequisites and benefits` },
  { group: 'character', name: 'Spells',    href: '/spells',    icon: Zap,    description: 'Every spell by school, level and components', count: (n) => `${n} by school and level` },
  { group: 'character', name: 'Powers',    href: '/powers',    icon: Brain,  description: 'Powers by level, with their costs and effects' },
  { group: 'character', name: 'Skills',    href: '/skills',    icon: Globe,  description: 'Checks, synergies and who has them' },
  { group: 'character', name: 'Languages', href: '/languages', icon: Scroll, description: 'Languages and their scripts' },
  { group: 'world', name: 'Monsters',   href: '/monsters',   icon: Skull,  description: 'Stat blocks by type and challenge rating', count: (n) => `${n} by type and challenge rating` },
  { group: 'world', name: 'Items',      href: '/items',      icon: Gem,    description: 'Magic items, weapons, armour and gear', count: (n) => `${n} magic items, weapons and gear` },
  { group: 'world', name: 'Deities',    href: '/deities',    icon: Crown,  description: 'Gods with their domains and alignments' },
  { group: 'world', name: 'Rulebooks',  href: '/rulebooks',  icon: Book,   description: 'The books everything comes from' },
  { group: 'world', name: 'Conditions', href: '/conditions', icon: Activity, description: 'Blinded, grappled, shaken: what each one does' },
  { group: 'world', name: 'Traps',      href: '/traps',      icon: AlertTriangle, description: 'Traps with their triggers, saves and effects' },
];

// The tools are not Families and none needs an account; a tool with `needs` shows only where the system has that family.
export const tools = [
  { name: 'Shop Generator', description: 'A shopkeeper and a stocked inventory', href: '/shop', icon: Store },
  { name: 'Treasure Generator', description: 'A hoard of items, gems and coins to fit the budget', href: '/treasure', icon: Coins },
  // Only where the system prints trap-design rules (issue 1584): `needs` names that family.
  { name: 'Trap Builder', description: 'A trap with its CR and cost worked out by the rules', href: '/trap-builder', icon: AlertTriangle, needs: 'traprule' },
  { name: 'Initiative Tracker', description: 'Run combat and share the order with your players', href: '/tracker', icon: Swords },
];

// Suggested searches under the hero box: well-known entries the d20 systems
// both have. A chip appears only when its Family exists in the active system.
const suggestions = [
  { name: 'Fireball', segment: 'spells', slug: 'fireball' },
  { name: 'Power Attack', segment: 'feats', slug: 'power-attack' },
  { name: 'Owlbear', segment: 'monsters', slug: 'owlbear' },
  { name: 'Cure Light Wounds', segment: 'spells', slug: 'cure-light-wounds' },
];

// "Everything a campaign needs, connected": one panel per part of play.
const panels = (withTable) => [
  {
    key: 'characters', name: 'Character sheets', href: '/characters', icon: User, cta: 'Build a character',
    image: { src: '/images/home/sheet.webp', width: 600, height: 410, alt: 'A character sheet: ability scores with their totals and modifiers worked out' },
    text: 'Build a character step by step on a sheet that does the maths. Pick from the same rules you read here and bring the character to any table.',
    bullets: ['Totals, modifiers and saves worked out for you', 'Spells, gear and abilities picked from the rules'],
  },
  {
    key: 'campaigns', name: 'Campaigns & wiki', href: '/campaigns', icon: BookOpen, cta: 'Start a campaign',
    image: { src: '/images/home/wiki.webp', width: 720, height: 462, alt: "A campaign wiki page about an NPC, with an infobox and the pages that link to it" },
    text: 'Gather your players, schedule sessions and keep recaps. Each campaign has a wiki of its world that the DM reveals a page at a time.',
    bullets: ['Sessions, recaps and notes in one place', 'Players only see what the DM has shared'],
  },
  ...(withTable ? [{
    key: 'table', name: 'Virtual tabletop', href: '/guide#play', icon: MapIcon, cta: 'How the tabletop works', badge: 'Early access',
    image: { src: '/images/home/tabletop.webp', width: 720, height: 392, alt: 'The virtual tabletop: a grid with tokens, the dice bag and the initiative strip' },
    text: 'Each campaign has one table, with maps, tokens, dice and turns. It knows the rules, so attacks, saves and areas work themselves out.',
    bullets: ['Initiative and actions tracked for you', 'Line of sight, light and hidden tokens'],
  }] : []),
];

/**
 * The rules tiles for a system (issue 1549), by the menus' rule (issue 1545):
 * a kind is a tile only when the system has a family for it, under that
 * family's name. `whole` when the system has every d20 kind the page offers
 * (Traps aside, which only the d20 systems have) — the page then reads as it always
 * has; otherwise it names the system.
 */
/** The DM tools a system offers: a tool that `needs` a family shows only where the system has it. */
export function toolsFor(system) {
  return tools.filter((t) => !t.needs || getFamilyByKey(t.needs, system) != null);
}

// Kinds only some d20 systems have, so their absence doesn't make a system partial.
const OPTIONAL = ['/traps', '/conditions', '/powers'];

export function homeFor(system, systems) {
  const families = allFamilies(system);
  const tiles = features.flatMap((f) => {
    const family = families.find((x) => `/${x.segment}` === f.href);
    return family ? [{ ...f, segment: family.segment, name: family.labels?.plural || f.name }] : [];
  });
  const core = features.filter((f) => !OPTIONAL.includes(f.href)).length;
  const whole = tiles.filter((t) => !OPTIONAL.includes(t.href)).length === core;
  const name = systems.find((s) => s.slug === system)?.name || null;
  if (whole || !name) return { tiles, own: null };
  // The d20 tiles' descriptions name d20 things (favoured classes, challenge
  // ratings): another system's tiles say plainly what is there.
  const kinds = tiles.map((t) => t.name.toLowerCase());
  return {
    tiles: tiles.map((t) => ({ ...t, description: `Browse, search and filter the ${t.name.toLowerCase()}`, count: null })),
    own: { name, kinds },
  };
}

/** "a, b and c". */
const listed = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const fmt = (n) => Number(n).toLocaleString('en-US');

/** The hero box's placeholder: the two biggest counted kinds by number, then the rest by name. */
export function searchPlaceholder(counts, families) {
  const label = (seg) => families.find((f) => f.segment === seg)?.labels?.plural?.toLowerCase();
  const kinds = ['spells', 'monsters', 'feats', 'items'].filter((s) => label(s));
  if (!kinds.length) return 'Search the rules…';
  const parts = kinds.map((s, i) => (i < 2 && counts && counts[s] ? `${fmt(counts[s])} ${label(s)}` : label(s)));
  return `Search ${parts.join(', ')}…`;
}

/** A link that, signed out, goes to sign-up first and then on (`state.from`). */
const Through = ({ signedIn, to, children, ...rest }) => (
  <Link to={signedIn ? to : '/register'} state={signedIn ? undefined : { from: to }} {...rest}>{children}</Link>
);

const Home = () => {
  // After deleting an account (issue 782) the person lands here, told once.
  const accountDeleted = useLocation().state?.accountDeleted === true;
  const { isAuthenticated, user } = useAuth();
  const { systems = [] } = useSystem();
  const system = getActiveSystem();
  const families = allFamilies(system);
  const { tiles, own } = homeFor(system, systems);
  const systemName = systems.find((s) => s.slug === system)?.name || null;
  // A system still under a placeholder name is not advertised on the first
  // screen or in the count (trillhc, 2026-10-26); it appears once it is named.
  const advertised = systems.filter((s) => !/\(working name\)/i.test(s.name || ''));
  const builtIn = advertised.filter((s) => isBuiltInSystem(s.slug)).map((s) => s.name);
  const eyebrow = [...(own ? [own.name] : builtIn), 'Free'].join(' · ');

  // One fetch on the visitor's page (the API-call budget): every number on it.
  // The dashboard shows no counts and does not ask.
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (isAuthenticated) return undefined;
    let live = true;
    apiClient.get('/api/search/counts')
      .then((res) => { if (live) setCounts(res.data?.counts || {}); })
      .catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [system, isAuthenticated]);

  // `/` puts the cursor in the hero search, as it does in the wiki's.
  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const label = (seg) => families.find((f) => f.segment === seg)?.labels?.plural;
  const strip = ['spells', 'feats', 'monsters', 'items'].filter((s) => label(s));
  const chips = own ? [] : suggestions.filter((c) => label(c.segment));
  const startTo = isAuthenticated ? '/characters' : '/register';
  const startLabel = isAuthenticated ? 'Your characters' : 'Start free';
  const groups = [
    { key: 'character', name: 'Character building', tiles: tiles.filter((t) => t.group === 'character') },
    { key: 'world', name: 'World building', tiles: tiles.filter((t) => t.group === 'world') },
  ].filter((g) => g.tiles.length);

  const head = (
    <Helmet>
      <title>{`The Dragon's Table — ${own ? own.name : '3.5e & Pathfinder 1e'} Reference`}</title>
      <meta name="description" content={own
        ? `A ${own.name} tabletop RPG reference with ${listed(own.kinds)}.`
        : 'A 3.5e & Pathfinder 1e tabletop RPG reference with spells, feats, classes, races, monsters, items, and more.'} />
    </Helmet>
  );

  if (isAuthenticated) {
    return (
      <>
        {head}
        <HomeDashboard user={user} tiles={tiles} tools={toolsFor(system)} searchRef={searchRef}
          placeholder={searchPlaceholder(null, families)} />
      </>
    );
  }

  return (
  <>
    {accountDeleted && (
      <p role="status" data-testid="account-deleted" className="card text-sm mb-6">
        Your account has been deleted.
      </p>
    )}
    {head}

    <div className="home">

      {/* ── Opening ───────────────────────────────────────────────── */}
      <section className="home-hero" aria-labelledby="home-title" data-testid="home-hero">
        <div className="home-hero-text">
          <p className="home-eyebrow" data-testid="home-eyebrow">{eyebrow}</p>
          <h1 id="home-title" className="home-headline">
            Your whole table,<span className="home-headline-gold"> in one place.</span>
          </h1>
          <p className="home-pitch">
            Look up any rule in seconds, build characters that do the maths, and run your campaign,
            its wiki and its tabletop, all from the same rules.
          </p>
          <div className="home-search" data-testid="home-search">
            <OmniSearch
              large
              idPrefix="home-search"
              inputRef={searchRef}
              ariaLabel="Search the rules"
              placeholder={searchPlaceholder(counts, families)}
            />
            <kbd className="home-search-key" aria-hidden="true">/</kbd>
          </div>
          {chips.length > 0 && (
            <ul className="home-chips" aria-label="Try">
              {chips.map((c) => (
                <li key={`${c.segment}/${c.slug}`}>
                  <Link className="home-chip" to={`/${c.segment}?slug=${c.slug}`} state={{ from: '/' }}>{c.name}</Link>
                </li>
              ))}
            </ul>
          )}
          <div className="home-ctas">
            <Link to={startTo} state={isAuthenticated ? undefined : { from: '/characters' }} className="home-cta home-cta-gold" data-testid="home-start">
              {startLabel}
            </Link>
            <a href="#rules" className="home-cta home-cta-outline">Explore the rules</a>
          </div>
          <p className="home-fineprint">No card, no trial. Reading the rules never needs an account.</p>
        </div>
        <div className="home-shots">
          <figure className="home-shot home-shot-back">
            <img src="/images/home/wiki.webp" width="720" height="462" decoding="async"
              alt="A campaign wiki page about an NPC, with an infobox and the pages that link to it" />
          </figure>
          <figure className="home-shot home-shot-front">
            <img src="/images/home/tabletop.webp" width="720" height="392" decoding="async"
              alt="The virtual tabletop: a grid with tokens, the dice bag and the initiative strip" />
          </figure>
        </div>
      </section>

      {/* ── Number strip ──────────────────────────────────────────── */}
      <section className="home-strip" aria-label="What is here" data-testid="home-strip">
        <dl className="home-strip-list">
          {strip.map((s) => (
            <div key={s} className="home-strip-item">
              <dt>{label(s)}</dt>
              <dd data-testid={`home-count-${s}`}>{counts ? (counts[s] != null ? fmt(counts[s]) : '—') : ' '}</dd>
            </div>
          ))}
          <div className="home-strip-item">
            <dt>Game systems</dt>
            <dd data-testid="home-count-systems">{advertised.length || ' '}</dd>
          </div>
        </dl>
        <p className="home-strip-note">{systemName ? `Counted in ${systemName}, the system you are reading.` : ' '}</p>
      </section>

      {/* ── Connected ─────────────────────────────────────────────── */}
      <section className="home-section" aria-labelledby="home-connected" data-testid="home-panels">
        <h2 id="home-connected" className="home-h2">Everything a campaign needs, connected</h2>
        <div className="home-panels">
          {panels(vttConfigured()).map((p) => (
            <article key={p.key} className="home-panel" data-testid={`home-panel-${p.key}`}>
              <img className="home-panel-img" src={p.image.src} width={p.image.width} height={p.image.height}
                alt={p.image.alt} loading="lazy" decoding="async" />
              <div className="home-panel-body">
                <h3 className="home-h3">
                  <span className="home-ring home-ring-sm" aria-hidden="true"><p.icon /></span>
                  {p.name}
                  {p.badge && <span className="home-badge">{p.badge}</span>}
                </h3>
                <p>{p.text}</p>
                <ul className="home-bullets">{p.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
                <Through signedIn={isAuthenticated} to={p.href} className="home-panel-link">
                  {p.cta} <ArrowRight aria-hidden="true" />
                </Through>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── Look anything up ──────────────────────────────────────── */}
      {/* #rules is the Explore button's target; #compendium is the older
          anchor other pages link to (issue 1239). */}
      <section id="rules" className="home-section" aria-labelledby="home-rules" data-testid="home-rules">
        <h2 id="home-rules" className="home-h2">Look anything up</h2>
        <div id="compendium" className="home-groups">
          {groups.map((g) => (
            <div key={g.key} className="home-group" data-testid={`home-group-${g.key}`}>
              <h3 className="home-group-name">{g.name}</h3>
              <ul className="home-tiles">
                {g.tiles.map((t) => {
                  const n = counts?.[t.segment];
                  const line = t.count && n ? t.count(fmt(n)) : t.description;
                  return (
                    <li key={t.href}>
                      <Link to={t.href} className="home-tile" data-testid={`home-tile-${t.segment}`}>
                        <span className="home-ring" aria-hidden="true"><t.icon /></span>
                        <span className="home-tile-text">
                          <span className="home-tile-name">{t.name}</span>
                          <span className="home-tile-desc">{line}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <div className="home-group home-group-tools" data-testid="home-group-tools">
            <h3 className="home-group-name">DM tools <span className="home-group-note">no account needed</span></h3>
            <ul className="home-tiles home-tiles-tools" style={{ '--tool-count': toolsFor(system).length }}>
              {toolsFor(system).map((t) => (
                <li key={t.href}>
                  <Link to={t.href} className="home-tile">
                    <span className="home-ring" aria-hidden="true"><t.icon /></span>
                    <span className="home-tile-text">
                      <span className="home-tile-name">{t.name}</span>
                      <span className="home-tile-desc">{t.description}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {/* The rule builder (issue 375): visitors are sent to sign in and back. */}
          <BuildRulesTile />
        </div>
      </section>

      {/* ── Closing ───────────────────────────────────────────────── */}
      <section className="home-closing" aria-labelledby="home-closing" data-testid="home-closing">
        <h2 id="home-closing" className="home-h2">Pull up a chair.</h2>
        <p>Bring your players, your characters and your world. It costs nothing to join.</p>
        <div className="home-ctas home-ctas-center">
          <Link to={isAuthenticated ? '/campaigns' : '/register'} state={isAuthenticated ? undefined : { from: '/campaigns' }} className="home-cta home-cta-gold">
            {isAuthenticated ? 'Your campaigns' : 'Start free'}
          </Link>
          <Link to="/guide" className="home-cta home-cta-outline">What you can do here</Link>
        </div>
      </section>

    </div>
  </>
  );
};

export default Home;
